import React from 'react';
import { Box } from '@weave-mui/material';
import Modal from '@weave-mui/modal';
import Button, { buttonVariants } from '@weave-mui/button';
import Typography, { typographyVariants } from '@weave-mui/typography';

/**
 * WeaveConfirmationModal - A reusable modal component for confirmation dialogs using Weave UI
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when user confirms the action
 * @param {string} [props.title='Confirmation'] - Modal title
 * @param {string} props.message - Message to display in the modal
 * @param {React.ReactNode} [props.children] - Optional additional content to display
 * @param {string} [props.confirmButtonText='Confirm'] - Text for the confirm button
 * @param {string} [props.cancelButtonText='Cancel'] - Text for the cancel button
 * @param {boolean} [props.loading=false] - Whether the confirm action is in progress
 * @param {string} [props.loadingText] - Text to display when loading (defaults to confirmButtonText with '...')
 * @param {string} [props.confirmButtonColor='primary'] - Color variant for confirm button
 */
const WeaveConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirmation',
  message,
  children,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  loading = false,
  loadingText,
  confirmButtonColor = 'primary',
}) => {
  const actionButtons = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
      }}
    >
      <Button
        variant={buttonVariants.OUTLINED}
        onClick={onClose}
        disabled={loading}
      >
        {cancelButtonText}
      </Button>
      <Button
        variant={buttonVariants.CONTAINED}
        onClick={onConfirm}
        disabled={loading}
        color={confirmButtonColor}
      >
        {loading ? loadingText || `${confirmButtonText}...` : confirmButtonText}
      </Button>
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      header={title}
      actions={actionButtons}
      sx={{
        '& .MuiModalWeave-box': {
          width: '500px',
          maxWidth: '90vw',
        },
      }}
    >
      <Box>
        <Typography variant={typographyVariants.BODY} sx={{ mb: 2 }}>
          {message}
        </Typography>
        {children}
      </Box>
    </Modal>
  );
};

export default WeaveConfirmationModal;
